import React, { useState, useMemo } from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Search, Loader2, Mail, Eye, Code, Copy, Check, Send, Plus, Users } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { toast } from '@/hooks/ui/use-toast';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Checkbox } from '@/components/ui/checkbox';
import { supabase } from '@/integrations/supabase/client';
import { useAllUsers, useWaitlist } from '@/hooks/admin/useAdminData';

// Import auth templates as raw strings
import confirmSignupHtml from '../../../docs/email-crm/auth-templates/01-confirm-signup.html?raw';
import inviteUserHtml from '../../../docs/email-crm/auth-templates/02-invite-user.html?raw';
import magicLinkHtml from '../../../docs/email-crm/auth-templates/03-magic-link.html?raw';
import changeEmailHtml from '../../../docs/email-crm/auth-templates/04-change-email.html?raw';
import resetPasswordHtml from '../../../docs/email-crm/auth-templates/05-reset-password.html?raw';
import reauthenticationHtml from '../../../docs/email-crm/auth-templates/06-reauthentication.html?raw';

// Import payment templates as raw strings
import paymentSuccessfulHtml from '../../../docs/email-crm/auth-templates/07-payment-successful.html?raw';
import paymentFailedHtml from '../../../docs/email-crm/auth-templates/08-payment-failed.html?raw';
import subscriptionCancelledHtml from '../../../docs/email-crm/auth-templates/09-subscription-cancelled.html?raw';

interface SystemEmailTemplate {
  id: string;
  slug: string;
  name: string;
  category: 'auth' | 'payment' | 'notification' | 'marketing';
  subject: string;
  html: string;
  variables: string[];
  description: string;
  filePath: string;
}

const SYSTEM_TEMPLATES: SystemEmailTemplate[] = [
  {
    id: '1',
    slug: 'confirm-signup',
    name: 'Confirm Sign Up',
    category: 'auth',
    subject: 'Confirm Your Email - Tag-Inn',
    html: confirmSignupHtml,
    variables: ['{{ .ConfirmationURL }}', '{{ .Token }}', '{{ .TokenHash }}', '{{ .SiteURL }}', '{{ .Email }}'],
    description: 'Sent when a new user signs up to confirm their email address.',
    filePath: 'docs/email-crm/auth-templates/01-confirm-signup.html',
  },
  {
    id: '2',
    slug: 'invite-user',
    name: 'Invite User',
    category: 'auth',
    subject: "You're Invited to Join Tag-Inn",
    html: inviteUserHtml,
    variables: ['{{ .ConfirmationURL }}', '{{ .Token }}', '{{ .TokenHash }}', '{{ .SiteURL }}', '{{ .Email }}'],
    description: 'Sent when an admin invites a new user to the platform.',
    filePath: 'docs/email-crm/auth-templates/02-invite-user.html',
  },
  {
    id: '3',
    slug: 'magic-link',
    name: 'Magic Link',
    category: 'auth',
    subject: 'Your Sign-In Link - Tag-Inn',
    html: magicLinkHtml,
    variables: ['{{ .ConfirmationURL }}', '{{ .Token }}', '{{ .TokenHash }}', '{{ .SiteURL }}', '{{ .Email }}'],
    description: 'Sent when a user requests a passwordless sign-in link.',
    filePath: 'docs/email-crm/auth-templates/03-magic-link.html',
  },
  {
    id: '4',
    slug: 'change-email',
    name: 'Change Email',
    category: 'auth',
    subject: 'Confirm Your New Email - Tag-Inn',
    html: changeEmailHtml,
    variables: ['{{ .ConfirmationURL }}', '{{ .Token }}', '{{ .TokenHash }}', '{{ .SiteURL }}', '{{ .Email }}', '{{ .NewEmail }}'],
    description: 'Sent when a user requests to change their email address.',
    filePath: 'docs/email-crm/auth-templates/04-change-email.html',
  },
  {
    id: '5',
    slug: 'reset-password',
    name: 'Reset Password',
    category: 'auth',
    subject: 'Reset Your Password - Tag-Inn',
    html: resetPasswordHtml,
    variables: ['{{ .ConfirmationURL }}', '{{ .Token }}', '{{ .TokenHash }}', '{{ .SiteURL }}', '{{ .Email }}'],
    description: 'Sent when a user requests to reset their password.',
    filePath: 'docs/email-crm/auth-templates/05-reset-password.html',
  },
  {
    id: '6',
    slug: 'reauthentication',
    name: 'Reauthentication',
    category: 'auth',
    subject: 'Your Verification Code - Tag-Inn',
    html: reauthenticationHtml,
    variables: ['{{ .Token }}', '{{ .SiteURL }}', '{{ .Email }}'],
    description: 'Sent when a user needs to verify their identity for sensitive actions.',
    filePath: 'docs/email-crm/auth-templates/06-reauthentication.html',
  },
  // Payment templates
  {
    id: '7',
    slug: 'payment-successful',
    name: 'Payment Successful',
    category: 'payment',
    subject: 'Payment Confirmed - Tag-Inn {{ plan_name }}',
    html: paymentSuccessfulHtml,
    variables: ['{{ customer_name }}', '{{ plan_name }}', '{{ amount }}', '{{ billing_period }}', '{{ payment_date }}', '{{ next_billing_date }}', '{{ invoice_number }}', '{{ payment_method }}', '{{ dashboard_url }}', '{{ invoice_url }}'],
    description: 'Sent when a payment is successfully processed or subscription is renewed.',
    filePath: 'docs/email-crm/auth-templates/07-payment-successful.html',
  },
  {
    id: '8',
    slug: 'payment-failed',
    name: 'Payment Failed',
    category: 'payment',
    subject: 'Action Required: Payment Failed - Tag-Inn',
    html: paymentFailedHtml,
    variables: ['{{ customer_name }}', '{{ plan_name }}', '{{ amount }}', '{{ payment_date }}', '{{ payment_method }}', '{{ failure_reason }}', '{{ retry_date }}', '{{ access_end_date }}', '{{ update_payment_url }}', '{{ dashboard_url }}'],
    description: 'Sent when a subscription payment fails and requires user action.',
    filePath: 'docs/email-crm/auth-templates/08-payment-failed.html',
  },
  {
    id: '9',
    slug: 'subscription-cancelled',
    name: 'Subscription Cancelled',
    category: 'payment',
    subject: 'Your Tag-Inn Subscription Has Been Cancelled',
    html: subscriptionCancelledHtml,
    variables: ['{{ customer_name }}', '{{ plan_name }}', '{{ cancellation_date }}', '{{ access_end_date }}', '{{ days_remaining }}', '{{ resubscribe_url }}', '{{ dashboard_url }}', '{{ feedback_url }}'],
    description: 'Sent when a user cancels their subscription.',
    filePath: 'docs/email-crm/auth-templates/09-subscription-cancelled.html',
  },
];

const EmailTemplatesManagement: React.FC = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [previewDialog, setPreviewDialog] = useState<{ open: boolean; template: SystemEmailTemplate | null }>({ open: false, template: null });
  const [editDialog, setEditDialog] = useState<{ open: boolean; template: SystemEmailTemplate | null }>({ open: false, template: null });
  const [editedHtml, setEditedHtml] = useState('');
  const [copiedId, setCopiedId] = useState<string | null>(null);

  // Compose email state
  const [composeOpen, setComposeOpen] = useState(false);
  const [composeSubject, setComposeSubject] = useState('');
  const [composeHtml, setComposeHtml] = useState('');
  const [selectAllUsers, setSelectAllUsers] = useState(false);
  const [selectWaitlist, setSelectWaitlist] = useState(false);
  const [selectedEmails, setSelectedEmails] = useState<Set<string>>(new Set());
  const [recipientSearch, setRecipientSearch] = useState('');
  const [isSending, setIsSending] = useState(false);
  const [showPreview, setShowPreview] = useState(false);

  // Fetch users and waitlist
  const { data: users, isLoading: usersLoading } = useAllUsers();
  const { data: waitlist, isLoading: waitlistLoading } = useWaitlist();

  const allEmails = useMemo(() => {
    const emails: { email: string; source: 'user' | 'waitlist' }[] = [];
    users?.forEach(u => emails.push({ email: u.email, source: 'user' }));
    waitlist?.forEach(w => emails.push({ email: w.email, source: 'waitlist' }));
    // Remove duplicates
    const seen = new Set<string>();
    return emails.filter(e => {
      if (seen.has(e.email)) return false;
      seen.add(e.email);
      return true;
    });
  }, [users, waitlist]);

  const filteredEmails = useMemo(() => {
    if (!recipientSearch) return allEmails;
    return allEmails.filter(e => e.email.toLowerCase().includes(recipientSearch.toLowerCase()));
  }, [allEmails, recipientSearch]);

  const selectedRecipientCount = useMemo(() => {
    let count = selectedEmails.size;
    if (selectAllUsers && users) {
      count += users.filter(u => !selectedEmails.has(u.email)).length;
    }
    if (selectWaitlist && waitlist) {
      count += waitlist.filter(w => !selectedEmails.has(w.email) && !(selectAllUsers && users?.some(u => u.email === w.email))).length;
    }
    return count;
  }, [selectAllUsers, selectWaitlist, selectedEmails, users, waitlist]);

  const getRecipientsArray = (): string[] => {
    const recipients = new Set<string>(selectedEmails);
    if (selectAllUsers && users) {
      users.forEach(u => recipients.add(u.email));
    }
    if (selectWaitlist && waitlist) {
      waitlist.forEach(w => recipients.add(w.email));
    }
    return Array.from(recipients);
  };

  const filteredTemplates = SYSTEM_TEMPLATES.filter((template) =>
    template.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    template.slug.toLowerCase().includes(searchTerm.toLowerCase()) ||
    template.category.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const openEditDialog = (template: SystemEmailTemplate) => {
    setEditedHtml(template.html);
    setEditDialog({ open: true, template });
  };

  const copyToClipboard = async (template: SystemEmailTemplate) => {
    try {
      await navigator.clipboard.writeText(template.html);
      setCopiedId(template.id);
      toast.success('Copied to clipboard', { description: 'Paste this HTML into Supabase Auth Email Templates.' });
      setTimeout(() => setCopiedId(null), 2000);
    } catch (err) {
      toast.error('Failed to copy', { description: 'Could not copy to clipboard.' });
    }
  };

  const copySubjectToClipboard = async (subject: string) => {
    try {
      await navigator.clipboard.writeText(subject);
      toast.success('Subject copied', { description: subject });
    } catch (err) {
      toast.error('Failed to copy');
    }
  };

  const getCategoryBadgeVariant = (category: string) => {
    switch (category) {
      case 'auth': return 'default';
      case 'payment': return 'secondary';
      case 'notification': return 'outline';
      case 'marketing': return 'outline';
      default: return 'outline';
    }
  };

  const handleSendEmail = async () => {
    const recipients = getRecipientsArray();
    if (recipients.length === 0) {
      toast.error('No recipients selected');
      return;
    }
    if (!composeSubject.trim()) {
      toast.error('Subject is required');
      return;
    }
    if (!composeHtml.trim()) {
      toast.error('Email content is required');
      return;
    }

    setIsSending(true);
    try {
      const { data: { session } } = await supabase.auth.getSession();
      const { data, error } = await supabase.functions.invoke('admin-send-email', {
        headers: { Authorization: `Bearer ${session?.access_token}` },
        body: {
          recipients,
          subject: composeSubject,
          htmlContent: composeHtml,
        },
      });

      if (error) throw error;

      toast.success('Emails sent!', { description: data.message });
      setComposeOpen(false);
      resetComposeForm();
    } catch (err) {
      toast.error('Failed to send emails', { description: (err as Error).message });
    } finally {
      setIsSending(false);
    }
  };

  const resetComposeForm = () => {
    setComposeSubject('');
    setComposeHtml('');
    setSelectAllUsers(false);
    setSelectWaitlist(false);
    setSelectedEmails(new Set());
    setRecipientSearch('');
    setShowPreview(false);
  };

  const toggleEmail = (email: string) => {
    setSelectedEmails(prev => {
      const next = new Set(prev);
      if (next.has(email)) {
        next.delete(email);
      } else {
        next.add(email);
      }
      return next;
    });
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold">Email Templates</h1>
          <p className="text-muted-foreground">
            Tag-Inn system email templates and email sending.
          </p>
        </div>
        <Button onClick={() => setComposeOpen(true)}>
          <Plus className="h-4 w-4 mr-2" />
          Compose Email
        </Button>
      </div>

      {/* Instructions Card */}
      <Card className="border-blue-200 bg-blue-50/50 dark:border-blue-900 dark:bg-blue-950/20">
        <CardContent className="pt-4">
          <div className="flex gap-3">
            <Mail className="h-5 w-5 text-blue-600 mt-0.5" />
            <div className="text-sm">
              <p className="font-medium text-blue-900 dark:text-blue-100">System templates (reference only)</p>
              <p className="mt-1 text-blue-800 dark:text-blue-200">
                <strong>Auth templates:</strong> Copy to <strong>Supabase Dashboard</strong> → <strong>Authentication</strong> → <strong>Email Templates</strong>.
                They use Supabase variables like <code className="bg-blue-100 dark:bg-blue-900 px-1 rounded">{'{{ .ConfirmationURL }}'}</code>.
              </p>
              <p className="mt-1 text-blue-800 dark:text-blue-200">
                <strong>Payment templates:</strong> Used by Stripe webhooks. Variables like <code className="bg-blue-100 dark:bg-blue-900 px-1 rounded">{'{{ customer_name }}'}</code> are replaced at send time.
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle>System Templates</CardTitle>
            <div className="relative w-64">
              <Search className="absolute left-2 top-2.5 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Search templates..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-8"
              />
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Template</TableHead>
                <TableHead>Category</TableHead>
                <TableHead>Subject</TableHead>
                <TableHead>Variables</TableHead>
                <TableHead className="w-32">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filteredTemplates.map((template) => (
                <TableRow key={template.id}>
                  <TableCell>
                    <div>
                      <div className="flex items-center gap-2 font-medium">
                        <Mail className="h-4 w-4 text-muted-foreground" />
                        {template.name}
                      </div>
                      <p className="text-xs text-muted-foreground mt-1">{template.description}</p>
                    </div>
                  </TableCell>
                  <TableCell>
                    <Badge variant={getCategoryBadgeVariant(template.category)}>
                      {template.category}
                    </Badge>
                  </TableCell>
                  <TableCell>
                    <button
                      onClick={() => copySubjectToClipboard(template.subject)}
                      className="text-left text-sm hover:text-primary transition-colors cursor-pointer"
                      title="Click to copy subject"
                    >
                      {template.subject}
                    </button>
                  </TableCell>
                  <TableCell>
                    <div className="flex flex-wrap gap-1">
                      {template.variables.slice(0, 2).map((v) => (
                        <code key={v} className="text-xs bg-muted px-1 py-0.5 rounded">
                          {v.replace('{{ .', '').replace(' }}', '')}
                        </code>
                      ))}
                      {template.variables.length > 2 && (
                        <span className="text-xs text-muted-foreground">
                          +{template.variables.length - 2} more
                        </span>
                      )}
                    </div>
                  </TableCell>
                  <TableCell>
                    <div className="flex items-center gap-1">
                      <Button
                        variant="ghost"
                        size="sm"
                        className="h-8 w-8 p-0"
                        onClick={() => setPreviewDialog({ open: true, template })}
                        title="Preview"
                      >
                        <Eye className="h-4 w-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        className="h-8 w-8 p-0"
                        onClick={() => openEditDialog(template)}
                        title="View HTML"
                      >
                        <Code className="h-4 w-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        className="h-8 w-8 p-0"
                        onClick={() => copyToClipboard(template)}
                        title="Copy HTML"
                      >
                        {copiedId === template.id ? (
                          <Check className="h-4 w-4 text-green-600" />
                        ) : (
                          <Copy className="h-4 w-4" />
                        )}
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Preview Dialog */}
      <Dialog open={previewDialog.open} onOpenChange={(open) => setPreviewDialog({ open, template: open ? previewDialog.template : null })}>
        <DialogContent className="max-w-4xl max-h-[90vh]">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Mail className="h-5 w-5" />
              Preview: {previewDialog.template?.name}
            </DialogTitle>
            <DialogDescription>
              Subject: <strong>{previewDialog.template?.subject}</strong>
            </DialogDescription>
          </DialogHeader>
          <ScrollArea className="h-[60vh] border rounded-md bg-gray-100">
            {previewDialog.template?.html && (
              <iframe
                srcDoc={previewDialog.template.html}
                className="w-full h-full min-h-[500px] bg-white"
                title="Email Preview"
              />
            )}
          </ScrollArea>
          <DialogFooter>
            <Button variant="outline" onClick={() => setPreviewDialog({ open: false, template: null })}>
              Close
            </Button>
            <Button onClick={() => previewDialog.template && copyToClipboard(previewDialog.template)}>
              <Copy className="h-4 w-4 mr-2" />
              Copy HTML
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit/View HTML Dialog */}
      <Dialog open={editDialog.open} onOpenChange={(open) => setEditDialog({ open, template: open ? editDialog.template : null })}>
        <DialogContent className="max-w-4xl max-h-[90vh]">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Code className="h-5 w-5" />
              HTML: {editDialog.template?.name}
            </DialogTitle>
            <DialogDescription>
              File: <code className="text-xs bg-muted px-1 py-0.5 rounded">{editDialog.template?.filePath}</code>
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            <div className="space-y-2">
              <Label>Available Variables</Label>
              <div className="flex flex-wrap gap-2">
                {editDialog.template?.variables.map((v) => (
                  <code key={v} className="text-xs bg-muted px-2 py-1 rounded">
                    {v}
                  </code>
                ))}
              </div>
            </div>

            <div className="space-y-2">
              <Label>HTML Content</Label>
              <Textarea
                value={editedHtml}
                onChange={(e) => setEditedHtml(e.target.value)}
                className="font-mono text-sm h-[400px]"
                readOnly
              />
              <p className="text-xs text-muted-foreground">
                To edit, modify the file directly: <code>{editDialog.template?.filePath}</code>
              </p>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setEditDialog({ open: false, template: null })}>
              Close
            </Button>
            <Button onClick={() => {
              navigator.clipboard.writeText(editedHtml);
              toast.success('HTML copied to clipboard');
            }}>
              <Copy className="h-4 w-4 mr-2" />
              Copy HTML
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Compose Email Dialog */}
      <Dialog open={composeOpen} onOpenChange={(open) => { setComposeOpen(open); if (!open) resetComposeForm(); }}>
        <DialogContent className="max-w-4xl max-h-[90vh]">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Send className="h-5 w-5" />
              Compose Email
            </DialogTitle>
            <DialogDescription>
              Send an email to selected recipients.
            </DialogDescription>
          </DialogHeader>

          <div className="grid grid-cols-2 gap-6">
            {/* Left column: Recipients */}
            <div className="space-y-4">
              <div>
                <Label className="flex items-center gap-2 mb-3">
                  <Users className="h-4 w-4" />
                  Recipients
                  <Badge className="ml-auto">
                    {selectedRecipientCount} selected
                  </Badge>
                </Label>

                {/* Quick select groups */}
                <div className="space-y-2 mb-4">
                  <div className="flex items-center space-x-2">
                    <Checkbox
                      id="all-users"
                      checked={selectAllUsers}
                      onCheckedChange={(checked) => setSelectAllUsers(checked === true)}
                    />
                    <label htmlFor="all-users" className="text-sm cursor-pointer">
                      All registered users ({users?.length || 0})
                    </label>
                  </div>
                  <div className="flex items-center space-x-2">
                    <Checkbox
                      id="waitlist"
                      checked={selectWaitlist}
                      onCheckedChange={(checked) => setSelectWaitlist(checked === true)}
                    />
                    <label htmlFor="waitlist" className="text-sm cursor-pointer">
                      Waitlist ({waitlist?.length || 0})
                    </label>
                  </div>
                </div>

                {/* Individual email selection */}
                <div className="space-y-2">
                  <div className="relative">
                    <Search className="absolute left-2 top-2.5 h-4 w-4 text-muted-foreground" />
                    <Input
                      placeholder="Search emails..."
                      value={recipientSearch}
                      onChange={(e) => setRecipientSearch(e.target.value)}
                      className="pl-8"
                    />
                  </div>
                  <ScrollArea className="h-[200px] border rounded-md p-2">
                    {(usersLoading || waitlistLoading) ? (
                      <div className="flex items-center justify-center h-full">
                        <Loader2 className="h-4 w-4 animate-spin" />
                      </div>
                    ) : filteredEmails.length === 0 ? (
                      <p className="text-sm text-muted-foreground text-center py-4">No emails found</p>
                    ) : (
                      <div className="space-y-1">
                        {filteredEmails.map((item) => (
                          <div key={item.email} className="flex items-center space-x-2 py-1">
                            <Checkbox
                              id={item.email}
                              checked={selectedEmails.has(item.email) || (selectAllUsers && item.source === 'user') || (selectWaitlist && item.source === 'waitlist')}
                              onCheckedChange={() => toggleEmail(item.email)}
                              disabled={(selectAllUsers && item.source === 'user') || (selectWaitlist && item.source === 'waitlist')}
                            />
                            <label htmlFor={item.email} className="text-sm cursor-pointer flex-1 truncate">
                              {item.email}
                            </label>
                            <Badge variant="outline" className="text-xs">
                              {item.source}
                            </Badge>
                          </div>
                        ))}
                      </div>
                    )}
                  </ScrollArea>
                </div>
              </div>
            </div>

            {/* Right column: Email content */}
            <div className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="subject">Subject</Label>
                <Input
                  id="subject"
                  value={composeSubject}
                  onChange={(e) => setComposeSubject(e.target.value)}
                  placeholder="Enter email subject..."
                />
              </div>

              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <Label htmlFor="content">HTML Content</Label>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => setShowPreview(!showPreview)}
                  >
                    <Eye className="h-4 w-4 mr-1" />
                    {showPreview ? 'Edit' : 'Preview'}
                  </Button>
                </div>
                {showPreview ? (
                  <div className="border rounded-md h-[280px] bg-gray-50">
                    <iframe
                      srcDoc={composeHtml || '<p style="color: #666; padding: 20px;">Enter HTML content to preview...</p>'}
                      className="w-full h-full"
                      title="Email Preview"
                    />
                  </div>
                ) : (
                  <Textarea
                    id="content"
                    value={composeHtml}
                    onChange={(e) => setComposeHtml(e.target.value)}
                    placeholder="<html>...</html>"
                    className="font-mono text-sm h-[280px]"
                  />
                )}
              </div>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setComposeOpen(false)} disabled={isSending}>
              Cancel
            </Button>
            <Button onClick={handleSendEmail} disabled={isSending || selectedRecipientCount === 0}>
              {isSending ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin mr-2" />
                  Sending...
                </>
              ) : (
                <>
                  <Send className="h-4 w-4 mr-2" />
                  Send to {selectedRecipientCount} recipient{selectedRecipientCount !== 1 ? 's' : ''}
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default EmailTemplatesManagement;
